import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { ClickableQrCode } from "@/components/ui/ClickableQrCode";
import { Spinner } from "@/components/ui/Spinner";
import { markTimesheetAtContact, timesheetAtContactFields } from "@/lib/timesheets";
import {
  linkedInvoiceRueckfrageLabel,
  listDokumentBillingQuestions,
  openBillingQuestionBanner,
  dokumentBillingQuestionIsOpen,
  type DokumentBillingQuestion,
} from "@/lib/dokumentBilling";
import { dokumentDisplayNumber, getDokument } from "@/lib/dokumente";
import { useCurrentUser } from "@/lib/auth";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";
import { TimesheetActionLogList } from "@/features/timesheetFlow/components/TimesheetActionLogList";
import { TimesheetDetailFooter } from "@/features/timesheetFlow/components/TimesheetDetailFooter";
import { Dialog } from "@/components/ui/Dialog";
import {
  TimesheetPdfEmbed,
  timesheetAttachmentPdfApiPath,
} from "@/features/timesheetFlow/components/TimesheetPdfEmbed";
import { TimesheetBillingTab } from "@/features/timesheetFlow/components/TimesheetBillingTab";
import { BillingQuestionAnswerDialog } from "@/features/timesheetFlow/components/BillingQuestionAnswerDialog";
import { billingQuestionIsOpen } from "@/features/timesheetFlow/billingQuestionAnswerRows";
import { TimesheetExportActions } from "@/features/timesheetFlow/components/TimesheetExportActions";
import { SignatureCanvas } from "@/features/timesheetFlow/components/SignatureCanvas";
import {
  archiveTimesheet,
  createRevision,
  getActionLog,
  getCompanySignQr,
  getPublicLinks,
  getTimesheetDetail,
  listBillingQuestions,
  listTimesheetScanAttachments,
  logTimesheetPrint,
  markBillingSeen,
  normalizePortalUrl,
  runPrintByLocationWithFeedback,
  sendTimesheetMail,
  signCompany,
  unarchiveTimesheet,
  uploadTimesheetPdf,
  type ActionLogEntry,
  type BillingQuestion,
  type TimesheetScanAttachment,
} from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetPermissions } from "@/features/timesheetFlow/timesheetPermissions";
import type {
  TimesheetMaterialRecord,
  TimesheetRecord,
  TimesheetRowRecord,
} from "@/features/timesheetFlow/timesheetFlowTypes";
import { useTimesheetInvoiceDraftControls } from "@/features/timesheetFlow/useTimesheetInvoiceDraftControls";
import { TimesheetProjectFooterIcons } from "@/features/timesheetFlow/components/TimesheetProjectFooterIcons";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";
import {
  resolveTimesheetMailPdfUrl,
  resolveTimesheetRecordStatus,
  statusBadgeClass,
  timesheetShowsBillingTab,
  timesheetStatusLabel,
} from "@/pages/stundennachweise/timesheetFilterLogic";

type DetailTab = "overview" | "attachments" | "actions" | "history" | "billing";

const TAB_LABELS: Record<DetailTab, string> = {
  overview: "Übersicht",
  attachments: "Anhänge",
  actions: "Aktionen",
  history: "Historie",
  billing: "Abrechnung & Rückfragen",
};

function fmtDateDe(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-DE");
}

function formatPeriod(ts: TimesheetRecord): string {
  const from = fmtDateDe(ts.date_from);
  const to = fmtDateDe(ts.date_to);
  if (from && to) return from === to ? from : `${from} – ${to}`;
  return from || to || "—";
}

export function TimesheetDetailPanel({
  timesheetId,
  permissions,
  initialTab,
  onEdit,
  onRevision,
  onRefresh,
  onPatchListAtContact,
  onClose,
}: {
  timesheetId: number;
  permissions: TimesheetPermissions;
  initialTab?: DetailTab;
  onEdit: () => void;
  onRevision: (newId: number) => void;
  onRefresh: () => void;
  onPatchListAtContact?: (timesheetId: number, atContact: boolean) => void;
  onClose: () => void;
}) {
  const [ts, setTs] = useState<TimesheetRecord | null>(null);
  const [rows, setRows] = useState<TimesheetRowRecord[]>([]);
  const [materials, setMaterials] = useState<TimesheetMaterialRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>("overview");
  const [tabInitialized, setTabInitialized] = useState(false);
  const [signDataUrl, setSignDataUrl] = useState<string | null>(null);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [publicLinks, setPublicLinks] = useState<{ draft_url?: string; signed_url?: string }>({});
  const [actionLog, setActionLog] = useState<ActionLogEntry[]>([]);
  const [billingQuestions, setBillingQuestions] = useState<BillingQuestion[]>([]);
  const [invoiceBillingQuestions, setInvoiceBillingQuestions] = useState<DokumentBillingQuestion[]>(
    [],
  );
  const [invoiceDisplayNumber, setInvoiceDisplayNumber] = useState("");
  const [scanAttachments, setScanAttachments] = useState<TimesheetScanAttachment[]>([]);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [answerDialogOpen, setAnswerDialogOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { user } = useCurrentUser();
  const layers = useTimesheetFlowLayers();

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const bundle = await getTimesheetDetail(timesheetId);
      setTs(bundle.timesheet);
      setRows(bundle.rows || []);
      setMaterials(bundle.materials || []);
      const gdRechnungId = Number(bundle.timesheet.gd_rechnung_id ?? 0);
      const [log, questions, links, attachments, invoiceBundle] = await Promise.all([
        getActionLog(timesheetId).catch(() => []),
        permissions.canViewBillingTab
          ? listBillingQuestions(timesheetId, { includeAnswered: true }).catch((err: unknown) => {
              setError(
                err instanceof Error ? err.message : "Rückfragen konnten nicht geladen werden.",
              );
              return [];
            })
          : Promise.resolve([]),
        getPublicLinks(timesheetId).catch(() => ({})),
        listTimesheetScanAttachments(timesheetId).catch(() => []),
        permissions.canViewBillingTab && Number.isFinite(gdRechnungId) && gdRechnungId > 0
          ? Promise.all([
              listDokumentBillingQuestions(gdRechnungId, { includeAnswered: true }).catch(
                () => [] as DokumentBillingQuestion[],
              ),
              getDokument(gdRechnungId).catch(() => null),
            ]).then(([invoiceQuestions, doc]) => ({
              questions: invoiceQuestions,
              number: doc ? dokumentDisplayNumber(doc) : "",
            }))
          : Promise.resolve({ questions: [] as DokumentBillingQuestion[], number: "" }),
      ]);
      setActionLog(log);
      setBillingQuestions(questions);
      setInvoiceBillingQuestions(invoiceBundle.questions);
      setInvoiceDisplayNumber(
        invoiceBundle.number ||
          String(bundle.timesheet.gd_rechnung_display_number || "").trim(),
      );
      setPublicLinks(links);
      setScanAttachments(attachments);
      setSelectedAttachmentId((prev) => {
        if (prev != null && attachments.some((a) => a.id === prev)) return prev;
        return attachments[0]?.id ?? null;
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }, [timesheetId, permissions.canViewBillingTab]);

  useEffect(() => {
    setTabInitialized(false);
    setTab(initialTab ?? "overview");
    void reload();
  }, [reload, timesheetId, initialTab]);

  const normalizedStatus = ts ? resolveTimesheetRecordStatus(ts) : "draft";
  const status = normalizedStatus;
  const isSigned =
    (normalizedStatus !== "draft" &&
      normalizedStatus !== "ready" &&
      normalizedStatus !== "archived") ||
    Boolean(ts?.company_signed_at);
  const isArchived = status === "archived";
  const openBillingQuestions = billingQuestions.filter((q) => billingQuestionIsOpen(q));
  const openInvoiceBillingQuestions = invoiceBillingQuestions.filter(dokumentBillingQuestionIsOpen);
  const invoiceNumber =
    invoiceDisplayNumber || String(ts?.gd_rechnung_display_number || "").trim();
  const openBillingTotal = openBillingQuestions.length + openInvoiceBillingQuestions.length;
  const showBillingTab =
    permissions.canViewBillingTab && timesheetShowsBillingTab(normalizedStatus);

  useEffect(() => {
    if (!ts || tabInitialized) return;
    if (initialTab) {
      setTab(initialTab);
    } else {
      setTab(showBillingTab && openBillingTotal > 0 ? "billing" : "overview");
    }
    setTabInitialized(true);
  }, [ts, tabInitialized, openBillingTotal, showBillingTab, initialTab]);

  const showAttachmentsTab = scanAttachments.length > 0;

  useEffect(() => {
    if (tab === "billing" && !showBillingTab) setTab("overview");
  }, [tab, showBillingTab]);

  useEffect(() => {
    if (tab === "attachments" && !showAttachmentsTab) setTab("overview");
  }, [tab, showAttachmentsTab]);

  useEffect(() => {
    if (tab !== "billing" || !permissions.canBilling || !timesheetId) return;
    void markBillingSeen(timesheetId)
      .then(() => {
        setTs((prev) =>
          prev
            ? {
                ...prev,
                billing_seen_at: new Date().toISOString(),
              }
            : prev,
        );
        onRefresh();
      })
      .catch(() => undefined);
  }, [tab, permissions.canBilling, timesheetId, onRefresh]);

  useEffect(() => {
    if (isSigned || !timesheetId || !permissions.canManage) return;
    void getCompanySignQr(timesheetId).then((data) => {
      setQrUri(data.qr_data_uri || null);
      setQrUrl(data.url ? normalizePortalUrl(data.url) : null);
    });
  }, [isSigned, timesheetId, permissions.canManage]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handlePrint = async (locationId: number, copies: number) => {
    if (!ts) return;
    const links =
      publicLinks.draft_url || publicLinks.signed_url
        ? publicLinks
        : await getPublicLinks(timesheetId);
    const url = resolveTimesheetMailPdfUrl(ts, links);
    const job = await runPrintByLocationWithFeedback(locationId, copies, url);
    await logTimesheetPrint(timesheetId, {
      location_id: locationId,
      copies,
      printer_name: job.printer_name || job.printer || job.result?.printer || undefined,
    });
    await reload();
    onRefresh();
  };

  const handleSign = async () => {
    if (!signDataUrl) return;
    setBusy(true);
    try {
      await signCompany(timesheetId, signDataUrl);
      await uploadTimesheetPdf(timesheetId);
      await reload();
      onRefresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unterschrift fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const startPollCustomerSign = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      void getTimesheetDetail(timesheetId).then((b) => {
        if (b.timesheet.company_signed_at || b.timesheet.status === "signed") {
          if (pollRef.current) clearInterval(pollRef.current);
          void reload();
          onRefresh();
        }
      });
    }, 2000);
  };

  const handleSendMail = async (payload: {
    recipients: string[];
    cc?: string[];
    subject: string;
    message: string;
    pdfUrl: string;
    htmlBody?: string;
    reminder?: boolean;
    markContact?: boolean;
  }) => {
    setBusy(true);
    try {
      await sendTimesheetMail(timesheetId, {
        recipients: payload.recipients,
        cc: payload.cc,
        subject: payload.subject,
        message: payload.message,
        html_body: payload.htmlBody,
        pdf_url: payload.pdfUrl,
        reminder: payload.reminder,
        mark_contact: payload.markContact ?? !payload.reminder,
      });
      await reload();
      onRefresh();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Mail fehlgeschlagen.";
      setError(msg);
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const {
    footerInvoiceActions,
    handleInvoiceAction,
    invoiceBusy,
    choiceDialog,
  } = useTimesheetInvoiceDraftControls({
    timesheetId,
    timesheet: ts,
    normalizedStatus,
    canBilling: permissions.canBilling,
    onReload: reload,
    onRefresh,
    onError: setError,
  });

  const handleAtContactChange = useCallback(
    (checked: boolean) => {
      if (!ts) return;
      const previous = Boolean(ts.at_contact);
      setTs((prev) => (prev ? { ...prev, ...timesheetAtContactFields(checked) } : prev));
      onPatchListAtContact?.(ts.id, checked);
      void markTimesheetAtContact(ts.id, checked).catch(() => {
        setTs((prev) => (prev ? { ...prev, ...timesheetAtContactFields(previous) } : prev));
        onPatchListAtContact?.(ts.id, previous);
        setError("NW bei AP konnte nicht gespeichert werden.");
      });
    },
    [ts, onPatchListAtContact],
  );

  if (loading && !ts) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (!ts) {
    return <p className="text-sm text-status-red">{error || "Nicht gefunden."}</p>;
  }

  const pdfLink = isSigned ? publicLinks.signed_url : publicLinks.draft_url;
  const showActionsTab = permissions.canManage;
  const selectedAttachment =
    selectedAttachmentId != null
      ? scanAttachments.find((a) => a.id === selectedAttachmentId) || null
      : null;
  const tabs: DetailTab[] = [
    "overview",
    ...(showAttachmentsTab ? (["attachments"] as const) : []),
    ...(showActionsTab ? (["actions"] as const) : []),
    "history",
    ...(showBillingTab ? (["billing"] as const) : []),
  ];
  const showSignDialog = permissions.canManage && !isSigned;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-separator px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-label">Stundennachweis</span>
          <span className="text-sm font-semibold tabular-nums text-label">
            {ts.display_number || `SN ${ts.id}`}
          </span>
          <TimesheetBadge
            className={statusBadgeClass(normalizedStatus, user)}
          >
            {timesheetStatusLabel(normalizedStatus)}
          </TimesheetBadge>
          <span className="text-sm text-label-secondary">{formatPeriod(ts)}</span>
        </div>
      </div>

      {isArchived ? (
        <p className="mx-4 mt-2 shrink-0 rounded-ios bg-fill-tertiary px-3 py-2 text-sm text-label-secondary">
          Dieser Stundennachweis ist archiviert und nur noch lesbar.
        </p>
      ) : null}

      {showBillingTab && openBillingTotal > 0 ? (
        <p className="mx-4 mt-2 shrink-0 rounded-ios bg-status-red/10 px-3 py-2 text-sm text-status-red">
          {openBillingQuestionBanner(
            openBillingQuestions.length,
            openInvoiceBillingQuestions.length,
            invoiceNumber,
          )}
          {" — "}
          <button
            type="button"
            className="font-medium underline"
            onClick={() => {
              if (permissions.canAnswerBillingQuestions && openBillingQuestions.length > 0) {
                setAnswerDialogOpen(true);
              } else {
                setTab("billing");
              }
            }}
          >
            {permissions.canAnswerBillingQuestions && openBillingQuestions.length > 0
              ? "beantworten"
              : "anzeigen"}
          </button>
        </p>
      ) : null}

      {error ? <p className="mx-4 mt-2 shrink-0 text-sm text-status-red">{error}</p> : null}

      <nav
        className="flex shrink-0 flex-wrap gap-1 border-b border-separator px-4 pb-0 pt-2"
        aria-label="Bereiche"
      >
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            className={clsx(
              "rounded-t-ios px-3 py-2 text-sm font-medium transition",
              tab === key
                ? "bg-card-elevated text-label ring-1 ring-separator ring-b-0"
                : "text-label-secondary hover:bg-fill-tertiary",
            )}
            onClick={() => setTab(key)}
          >
            {TAB_LABELS[key]}
            {key === "attachments" && scanAttachments.length > 0 ? (
              <span className="ml-1.5 inline-flex min-w-[1.25rem] justify-center rounded-full bg-fill-tertiary px-1.5 py-0.5 text-[10px] font-semibold text-label-secondary">
                {scanAttachments.length}
              </span>
            ) : null}
            {key === "billing" && openBillingTotal > 0 ? (
              <span className="ml-1.5 inline-flex min-w-[1.25rem] justify-center rounded-full bg-status-red px-1.5 py-0.5 text-[10px] font-semibold text-white">
                {openBillingTotal}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      <div
        className={clsx(
          "relative flex min-h-0 flex-1 flex-col",
          tab !== "overview" && tab !== "attachments" && "overflow-y-auto px-4 py-3",
        )}
      >
        <div
          className={clsx(
            "flex min-h-0 flex-1 flex-col",
            tab !== "overview" && "hidden",
          )}
          aria-hidden={tab !== "overview"}
        >
          <TimesheetPdfEmbed timesheetId={timesheetId} isSigned={isSigned} bare className="min-h-0 flex-1" />
        </div>

        <div
          className={clsx(
            "flex min-h-0 flex-1 flex-col",
            tab !== "attachments" && "hidden",
          )}
          aria-hidden={tab !== "attachments"}
        >
          {showAttachmentsTab && selectedAttachment ? (
            <>
              {scanAttachments.length > 1 ? (
                <div className="flex shrink-0 flex-wrap gap-1 border-b border-separator px-3 py-2">
                  {scanAttachments.map((att, index) => (
                    <button
                      key={att.id}
                      type="button"
                      className={clsx(
                        "rounded-ios px-2.5 py-1 text-xs font-medium transition",
                        selectedAttachmentId === att.id
                          ? "bg-fill-tertiary text-label"
                          : "text-label-secondary hover:bg-fill-quaternary",
                      )}
                      onClick={() => setSelectedAttachmentId(att.id)}
                    >
                      Anhang {index + 1}
                    </button>
                  ))}
                </div>
              ) : null}
              <TimesheetPdfEmbed
                key={selectedAttachment.id}
                timesheetId={timesheetId}
                isSigned
                apiPath={timesheetAttachmentPdfApiPath(timesheetId, selectedAttachment.id)}
                title={selectedAttachment.filename || "Anhang PDF"}
                bare
                className="min-h-0 flex-1"
              />
            </>
          ) : null}
        </div>

        {tab === "actions" && showActionsTab ? (
          <div className="space-y-3">
            <p className="text-xs text-label-secondary">
              Primär zuerst: PDF in der Übersicht prüfen, dann Versand oder Druck ausführen.
            </p>
            <TimesheetExportActions
              timesheetId={timesheetId}
              timesheet={ts}
              displayNumber={ts.display_number || String(ts.id)}
              busy={busy}
              onPrint={handlePrint}
              onSendMail={handleSendMail}
              onMailSent={async () => {
                await reload();
                onRefresh();
              }}
              onError={setError}
            />
          </div>
        ) : null}

        {tab === "history" ? (
          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-label">Historie</h3>
              <Button type="button" size="sm" variant="secondary" onClick={() => void reload()}>
                Aktualisieren
              </Button>
            </div>
            <TimesheetActionLogList
              entries={actionLog}
              timesheetLabel={ts.display_number || String(ts.id)}
            />
          </section>
        ) : null}

        {tab === "billing" && showBillingTab && ts ? (
          <div className="eh-ts-billing-tab-root flex min-h-0 w-full flex-1 flex-col">
            <TimesheetBillingTab
              timesheet={ts}
              rows={rows}
              materials={materials}
              permissions={permissions}
              onReload={reload}
              onRefresh={onRefresh}
              onError={setError}
              onOpenAnswerDialog={
                permissions.canAnswerBillingQuestions
                  ? () => setAnswerDialogOpen(true)
                  : undefined
              }
              linkedInvoiceQuestions={invoiceBillingQuestions}
              linkedInvoiceLabel={linkedInvoiceRueckfrageLabel(invoiceNumber)}
            />
          </div>
        ) : null}
      </div>

      <TimesheetDetailFooter
        onClose={onClose}
        nwAtContact={
          permissions.canManage && status === "ready"
            ? {
                checked: Boolean(ts.at_contact),
                onChange: handleAtContactChange,
              }
            : undefined
        }
        iconActions={<TimesheetProjectFooterIcons timesheet={ts} />}
        actions={[
          {
            id: "answer-billing",
            label: "Rückfragen beantworten",
            variant: "primary" as const,
            onClick: () => setAnswerDialogOpen(true),
            hidden: !(permissions.canAnswerBillingQuestions && openBillingQuestions.length > 0),
          },
          {
            id: "edit",
            label: "Bearbeiten",
            onClick: onEdit,
            hidden: true,
          },
          {
            id: "sign",
            label: "Unterschrift",
            onClick: () => setSignDialogOpen(true),
            hidden: !showSignDialog,
          },
          {
            id: "revision",
            label: "Auf Entwurf setzen",
            onClick: () => {
              setBusy(true);
              setError(null);
              void createRevision(timesheetId)
                .then((r) => onRevision(r.id))
                .catch((err: unknown) => {
                  setError(
                    err instanceof Error
                      ? err.message
                      : "Zurücksetzen auf Entwurf ist fehlgeschlagen.",
                  );
                })
                .finally(() => setBusy(false));
            },
            hidden: !(permissions.canRevision && status === "ready"),
            disabled: busy,
          },
          {
            id: "archive",
            label: status === "archived" ? "Wiederherstellen" : "Archivieren",
            onClick: () => {
              setBusy(true);
              const fn = status === "archived" ? unarchiveTimesheet : archiveTimesheet;
              void fn(timesheetId).then(reload).finally(() => setBusy(false));
            },
            hidden: !permissions.canArchive,
            disabled: busy,
          },
          {
            id: "open-pdf",
            label: "PDF öffnen",
            onClick: () => {
              if (pdfLink) window.open(pdfLink, "_blank", "noopener");
            },
            hidden: !pdfLink,
          },
          ...footerInvoiceActions.map((action) => ({
            id: `invoice-${action.mode}${action.provider ? `-${action.provider}` : ""}`,
            label: action.label,
            variant: (action.mode === "create" ? "primary" : "secondary") as "primary" | "secondary",
            disabled: busy || invoiceBusy,
            onClick: () => handleInvoiceAction(action),
          })),
        ]}
      />

      {choiceDialog}

      <Dialog
        open={signDialogOpen}
        onClose={() => setSignDialogOpen(false)}
        dirty={!!signDataUrl}
        title="Unterschrift"
        size="md"
        zIndexClass={layers.overlay}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setSignDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleSign().then(() => setSignDialogOpen(false));
              }}
              disabled={!signDataUrl || busy}
            >
              Speichern
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <SignatureCanvas onChange={(_, url) => setSignDataUrl(url)} />
          <Button type="button" size="sm" variant="secondary" onClick={startPollCustomerSign}>
            Kundensignatur abwarten
          </Button>
          {qrUri ? (
            <div className="flex flex-col items-center gap-2 border-t border-separator pt-3">
              <p className="text-xs text-label-secondary">QR für Kundensignatur</p>
              <ClickableQrCode src={qrUri} href={qrUrl} alt="Signatur-QR" className="h-40 w-40" />
              {qrUrl ? (
                <a href={qrUrl} target="_blank" rel="noopener noreferrer" className="break-all text-xs text-accent">
                  {qrUrl}
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      </Dialog>

      {ts && permissions.canAnswerBillingQuestions ? (
        <BillingQuestionAnswerDialog
          open={answerDialogOpen}
          onClose={() => setAnswerDialogOpen(false)}
          timesheet={ts}
          materials={materials}
          onSaved={() => {
            void reload();
            onRefresh();
          }}
          onShowPdf={() => {
            setAnswerDialogOpen(false);
            setTab("overview");
          }}
          onError={setError}
        />
      ) : null}

    </div>
  );
}

